import React, { useEffect } from 'react';
import styles from './RuleEditor.module.scss';
import { ReactComponent as DeleteIcon } from 'containers/channels/trash.svg'
import AppSelect from 'elements/appSelect';
import WorkflowDataSelector from 'containers/Workflows/WorkflowDataSelector/WorkflowDataSelector';
import ContentDataSelector from 'containers/Workflows/ContentDataSelector/ContentDataSelector';
import { AdminMessagingService } from 'api/services/admin-messaging/AdminMessagingService';

// ruleEditor context api
const RuleEditorContext = React.createContext<any>(null);

const RuleEditor = (
    {
        workflowData,
        topActionsDetails,
        setTopActionsDetails,
        groupData,
        workflowEventID
    }: {
        workflowData: any,
        topActionsDetails: any,
        setTopActionsDetails: any,
        groupData: any
        workflowEventID: any
    }) => {

    // workflow filter condition
    const [workflowConditions, setWorkflowConditions] = React.useState<any>(null);
    const [filterFields, setFilterFields] = React.useState<any>(null);

    React.useEffect(() => {
        if (
            groupData &&
            workflowEventID &&
            workflowData.status !== 'active'
        ) {
            fetchWorkflowFilterFields(workflowEventID, '')
        }
    }, [workflowEventID, workflowData && workflowData.status])


    React.useEffect(() => {
        if (workflowData && workflowData.status !== 'active') {
            fetchWorkflowFilterConditions('')
        }
    }, [workflowData && workflowData.status])

    const fetchWorkflowFilterFields = (fieldId: number, query: string) => {
        AdminMessagingService.getInstance().fetchWorkflowFilterFields(groupData.id, fieldId, query)
            .then((response: any) => {
                function _getFirstKeyValue(data: any) {
                    const keys = Object.keys(data);
                    if (keys.length > 0) {
                        return keys.map(key => ({
                            key,
                            data: _transformData(data[key])
                        }));
                    }
                    return [];
                }

                function _transformData(data: any) {
                    return data.map((item: any) => ({
                        label: item.name || '',
                        value: item.name || '',
                        ...item
                    }));
                }
                const result = _getFirstKeyValue(response.data);
                setFilterFields(result);
            }).catch((error: any) => {
                console.error(error);
            }).finally(() => {

            })
    }

    const fetchWorkflowFilterConditions = (query: string) => {
        AdminMessagingService.getInstance().fetchWorkflowFilterConditions(groupData.id, query)
            .then((response: any) => {

                function _newData(data: any) {
                    return data.map((item: any) => ({
                        label: item.name || '',
                        value: item.name || '',
                        ...item
                    }));
                }
                const result = _newData(response.data);
                setWorkflowConditions(result);
            }).catch((error: any) => {
                console.error(error);
            }).finally(() => {

            })
    }

    const generateRandomId = () => {
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }

    function constructConditionStringWithRuleType(data: any): string {
        const conditionGroups = data.condition_groups;
        if (conditionGroups.length > 0) {
            const result: string[] = [];

            conditionGroups.forEach((group: any) => {
                const rules = group.rules;
                const ruleStrings = rules
                    .map((rule: any) => {
                        const conditionLabel = rule.condition_label || '';
                        // const value = rule.value || '';
                        // const fieldLabel = rule.field_label || '';
                        const value = rule.value ? `"${rule.value}"` : '';
                        const fieldLabel = rule.field_label ? `'${rule.field_label}'` : '';
                        return `${fieldLabel} ${conditionLabel} ${value}`
                    });

                if (ruleStrings.length > 0) {
                    result.push(`${group.ruleType} ${ruleStrings.join(' AND ')}`);
                }
            });

            return result.join(' ');
        }

        return ''; // Return an empty string if no condition groups exist
    }

    const result = constructConditionStringWithRuleType(topActionsDetails);

    const addRule = (groupId: any) => {
        setTopActionsDetails((prevState: any) => ({
            ...prevState,
            executed_resource_name: result,
            condition_groups: prevState.condition_groups.map((group: any) =>
                group.randomID === groupId
                    ? {
                        ...group,
                        rules: [
                            ...group.rules,
                            {
                                randomID: generateRandomId(),
                                field: null,
                                condition: null,
                                value: ""
                            }
                        ]
                    }
                    : group
            )
        }));
    };

    const addRuleGroup = () => {
        setTopActionsDetails((prevState: any) => ({
            ...prevState,
            executed_resource_name: result,
            condition_groups: [
                ...(Array.isArray(prevState.condition_groups) ? prevState.condition_groups : []),
                {
                    randomID: generateRandomId(),
                    ruleType: 'Or continue if',
                    rules: [
                        {
                            randomID: generateRandomId(),
                            field: null,
                            condition: null,
                            value: ""
                        },

                    ]
                }
            ]
        }));
    };

    const removeRule = (groupId: any, ruleId: any) => {
        setTopActionsDetails((prevState: any) => ({
            ...prevState, // Keep root-level fields like "active" and "choiceType"
            condition_groups: prevState.condition_groups
                .map((group: any) => {
                    if (group.randomID === groupId) {
                        // Remove the specified rule from the group's 'rules' array
                        const updatedRules = group.rules.filter((rule: any) => rule.randomID !== ruleId);

                        // If the group has 'Or' type and no rules left, mark it for removal
                        if (group.ruleType.includes('Or') && updatedRules.length === 0) {
                            return null; // Mark for removal
                        }

                        // Otherwise, return the updated group with remaining rules
                        return {
                            ...group,
                            rules: updatedRules
                        };
                    }

                    // Return other groups unchanged
                    return group;
                })
                .filter((group: any) => group !== null) // Remove any groups marked as null
        }));
    };

    const getConditionValue = (groupId: string, ruleId: string) => {
        // Iterate over condition_groups to find the matching group
        const group = topActionsDetails.condition_groups.find((group: any) => group.randomID === groupId);

        if (group) {
            // Iterate over rules in the found group to find the matching rule
            const rule = group.rules.find((rule: any) => rule.randomID === ruleId);

            if (rule) {
                // Return both the value and a flag indicating if it's non-empty
                return {
                    condition: rule.condition_label,
                    hasValue: rule.condition_label && rule.condition_label.trim() !== '',  // Check if the value is non-empty
                };
            }
        }

        // Return null or an object with empty value and false flag if group or rule is not found
        return {
            condition: null,
            hasValue: false,
        };
    };

    const getFieldValue = (groupId: string, ruleId: string) => {
        // Iterate over condition_groups to find the matching group
        const group = topActionsDetails.condition_groups.find((group: any) => group.randomID === groupId);

        if (group) {
            // Iterate over rules in the found group to find the matching rule
            const rule = group.rules.find((rule: any) => rule.randomID === ruleId);

            if (rule) {
                // Return both the value and a flag indicating if it's non-empty
                return {
                    field: rule.field_label,
                    hasValue: rule.field_label && rule.field_label.trim() !== '',  // Check if the value is non-empty
                };
            }
        }

        // Return null or an object with empty value and false flag if group or rule is not found
        return {
            field: null,
            hasValue: false,
        };
    };

    useEffect(() => {
        setTopActionsDetails((prevState: any) => ({
            ...prevState,
            executed_resource_name: result,
        }));
    }, [result])

    return (
        <RuleEditorContext.Provider
            value={{
                workflowData,
                topActionsDetails,
                setTopActionsDetails,
                addRuleGroup,
                getConditionValue,
                getFieldValue,
                groupData,
                workflowEventID,
                workflowConditions,
                setWorkflowConditions,
                filterFields,
                setFilterFields,
                fetchWorkflowFilterFields,
                fetchWorkflowFilterConditions
            }}
        >
            <div className={styles.ruleEditor}>
                {
                    topActionsDetails &&
                    topActionsDetails.condition_groups &&
                    topActionsDetails.condition_groups.map((group: any) => (
                        <div key={group.randomID} className={`${styles.ruleGroup} ${group.ruleType.includes('Or') ? styles.ruleGroup__Or : ''}`}>

                            {
                                group.rules.map((rule: any, index: number) => (
                                    <div
                                        className={`${styles.rule} ${group.ruleType.includes('Or') ? styles.rule__Or : ''}`}
                                    >
                                        <div className={`${styles.rule__title}`}>
                                            {
                                                index === 0 ?
                                                    <h4>{group.ruleType.includes('Only') ? 'Only continue if' : 'Or continue if'}</h4>
                                                    :
                                                    <h4 className={styles.rule__title__add}>Add</h4>
                                            }
                                            {((group.ruleType.includes('Or')) || (group.ruleType.includes('Only') && group.rules.length > 1)) &&
                                                <button
                                                    onClick={() => removeRule(group.randomID, rule.randomID)}
                                                    className={`${styles.removeRuleButton} ${workflowData.status === 'active' ? styles.removeRuleButton__disabled : ''}`}
                                                    title="Remove rule"
                                                    disabled={workflowData.status === 'active'}
                                                >
                                                    <span>
                                                        <DeleteIcon />
                                                    </span>
                                                    Remove rule
                                                </button>
                                            }
                                        </div>

                                        <div className={styles.rule__body}>
                                            <FieldRuleItem
                                                conditionGroup={group}
                                                rule={rule}
                                            />

                                            <CondionRuleItem group={group} rule={rule} />

                                        </div>

                                        {
                                            rule.condition_value_type === "image"
                                                ?
                                                <></>
                                                :
                                                <input
                                                    className={styles.input}
                                                    disabled={workflowData.status === 'active'}
                                                    type="text"
                                                    placeholder="Enter text..."
                                                    value={rule.value}
                                                    onChange={(e: any) => {
                                                        const newValue = e.target.value;

                                                        setTopActionsDetails((prevState: any) => {
                                                            return {
                                                                ...prevState,
                                                                // executed_resource_name: result,
                                                                condition_groups: prevState.condition_groups.map((con_group: any) =>
                                                                    con_group.randomID === group.randomID
                                                                        ? {
                                                                            ...con_group,
                                                                            rules: con_group.rules.map((gp_rule: any) =>
                                                                                gp_rule.randomID === rule.randomID
                                                                                    ? { ...gp_rule, value: newValue }
                                                                                    : gp_rule
                                                                            ),
                                                                        }
                                                                        : con_group
                                                                ),
                                                            };
                                                        });
                                                    }}
                                                />
                                        }
                                    </div>
                                ))
                            }
                            <button
                                disabled={workflowData.status === 'active'}
                                onClick={() => addRule(group.randomID)}
                                className={`${styles.addButton} ${workflowData.status === 'active' ? styles.addButton__disabled : ''}`}
                            >
                                + And
                            </button>
                        </div>
                    ))}
                <button
                    disabled={workflowData.status === 'active'}
                    onClick={addRuleGroup}
                    className={`${styles.addOrGroupButton} ${workflowData.status === 'active' ? styles.addOrGroupButton__disabled : ''}`}
                >
                    + Add "Or" rule group
                </button>
            </div>
        </RuleEditorContext.Provider>
    );
};

const CondionRuleItem = ({ group, rule }: { group: any, rule: any }) => {

    const {
        workflowData,
        topActionsDetails,
        setTopActionsDetails,
        getConditionValue,
        workflowConditions,
        // fetchWorkflowFilterConditions
    } = React.useContext(RuleEditorContext);

    const hideDropdownRef = React.useRef<any>(null);
    const [workflowConditionsQuery, setWorkflowConditionsQuery] = React.useState<any>('');

    // React.useEffect(() => {
    //     if (
    //         workflowData.status !== 'active'
    //         && workflowConditionsQuery.trim() !== ''
    //         && workflowConditionsQuery.length > 0
    //     ) {
    //         fetchWorkflowFilterConditions(workflowConditionsQuery)
    //     }
    // }, [workflowConditionsQuery])

    function getFilteredConditions() {
        if (!workflowConditions) {
            return null;
        }
        return workflowConditions.filter((condition:any) => {
            if (rule.condition_value_type === 'image') {
                return condition.type === 'image';
            } else {
                return condition.type !== 'image';
            }
        });
    }

    return (
        <div
            className={styles.rule__item}>
            <AppSelect
                // popupStyles={{ minHeight: '100px' }}
                inputClassName={styles.inputActionTrigger}
                disabled={(workflowData.status === 'active') || (getConditionValue(group.randomID, rule.randomID).hasValue)}
                XIconstyles={` ${topActionsDetails && getConditionValue(group.randomID, rule.randomID).hasValue ? styles.XIconstyles : ""} ${workflowData.status === 'active' ? styles.hideXIconstyles : ""}`}
                placeholder="Search or select a condtion"
                hideDropdownRef={hideDropdownRef}
                value={
                    workflowConditionsQuery &&
                        workflowConditionsQuery.trim() !== ''
                        ?
                        workflowConditionsQuery
                        :
                        (topActionsDetails && getConditionValue(group.randomID, rule.randomID).hasValue) ?
                            (getConditionValue(group.randomID, rule.randomID).condition.charAt(0).toUpperCase() + getConditionValue(group.randomID, rule.randomID).condition.slice(1))
                            :
                            ''
                }
                markRequired
                cursorPointer
                inputIconClassName={`${styles.inputIconNameAction} ${topActionsDetails &&
                    getConditionValue(group.randomID, rule.randomID).hasValue ? styles.inputIconNameActionNone : ""} ${workflowData.status === 'active' ? styles.hideXIconstyles : ""}`}
                onChangeValue={(e: any) => setWorkflowConditionsQuery(e.target.value)}
                onRemoveSelectedValue={() => {
                    setWorkflowConditionsQuery('');
                    setTopActionsDetails((prevState: any) => {
                        return {
                            ...prevState,
                            // executed_resource_name: result,
                            condition_groups: prevState.condition_groups.map((con_group: any) =>
                                con_group.randomID === group.randomID
                                    ? {
                                        ...con_group,
                                        rules: con_group.rules.map((gp_rule: any) =>
                                            gp_rule.randomID === rule.randomID
                                                ? { ...gp_rule, condition: null, condition_label: null, value: null }
                                                : gp_rule
                                        ),
                                    }
                                    : con_group
                            ),
                        };
                    });
                }}
            >
                {
                    getFilteredConditions() !== null ?
                        <div className={styles.ActiontypesDropdown}>
                            <ContentDataSelector
                                data={getFilteredConditions()}
                                onDataSelected={(value: any, data: any) => {
                                    setWorkflowConditionsQuery('');
                                    hideDropdownRef.current();
                                    setTopActionsDetails((prevState: any) => {
                                        return {
                                            ...prevState,
                                            condition_groups: prevState.condition_groups.map((con_group: any) =>
                                                con_group.randomID === group.randomID
                                                    ? {
                                                        ...con_group,
                                                        rules: con_group.rules.map((gp_rule: any) =>
                                                            gp_rule.randomID === rule.randomID
                                                                ?
                                                                {
                                                                    ...gp_rule,
                                                                    condition: data.slug,
                                                                    condition_label: data.label,
                                                                }
                                                                :
                                                                gp_rule
                                                        ),
                                                    }
                                                    : con_group
                                            ),
                                        };
                                    });
                                }}
                                labelStyle={styles.eventResourceTypelabelStyle}
                                elementContainerClass={styles.eventResourceTypelabelStyle__Dropdown}
                            />
                        </div>
                        :
                        <></>
                }
            </AppSelect>
        </div>
    )
}

const FieldRuleItem = ({ conditionGroup, rule }: { conditionGroup: any, rule: any }) => {
    const {
        workflowData,
        topActionsDetails,
        setTopActionsDetails,
        getFieldValue,
        // groupData,
        // workflowEventID,
        filterFields,
        // fetchWorkflowFilterFields
    } = React.useContext(RuleEditorContext);

    const hideDropdownRef = React.useRef<any>(null);
    const [filterFieldsQuery, setFilterFieldsQuery] = React.useState<any>('');

    // React.useEffect(() => {
    //     if (
    //         groupData &&
    //         workflowEventID &&
    //         workflowData.status !== 'active' &&
    //         filterFieldsQuery.trim() !== '' &&
    //         filterFieldsQuery.length > 0
    //     ) {
    //         fetchWorkflowFilterFields(workflowEventID, filterFieldsQuery)
    //     }
    // }, [filterFieldsQuery])


    return (
        <div className={styles.rule__item}>
            <AppSelect
                popupStyles={{ minHeight: '100px' }}
                inputClassName={styles.inputActionTrigger}
                disabled={(workflowData.status === 'active') || (getFieldValue(conditionGroup.randomID, rule.randomID).hasValue)}
                XIconstyles={` ${topActionsDetails && getFieldValue(conditionGroup.randomID, rule.randomID).hasValue ? styles.XIconstyles : ""} ${workflowData.status === 'active' ? styles.hideXIconstyles : ""}`}
                placeholder="Search or select a field"
                hideDropdownRef={hideDropdownRef}
                value={
                    filterFieldsQuery &&
                        filterFieldsQuery.trim() !== ''
                        ?
                        filterFieldsQuery
                        :
                        (topActionsDetails && getFieldValue(conditionGroup.randomID, rule.randomID).hasValue) ?
                            (getFieldValue(conditionGroup.randomID, rule.randomID).field.charAt(0).toUpperCase() + getFieldValue(conditionGroup.randomID, rule.randomID).field.slice(1))
                            :
                            ''
                }
                markRequired
                cursorPointer
                inputIconClassName={`${styles.inputIconNameAction} ${topActionsDetails &&
                    getFieldValue(conditionGroup.randomID, rule.randomID).hasValue ? styles.inputIconNameActionNone : ""} ${workflowData.status === 'active' ? styles.hideXIconstyles : ""}`}
                onChangeValue={(e: any) => setFilterFieldsQuery(e.target.value)}
                onRemoveSelectedValue={() => {
                    setFilterFieldsQuery('');
                    setTopActionsDetails((prevState: any) => {
                        return {
                            ...prevState,
                            condition_groups: prevState.condition_groups.map((con_group: any) =>
                                con_group.randomID === conditionGroup.randomID
                                    ? {
                                        ...con_group,
                                        rules: con_group.rules.map((gp_rule: any) =>
                                            gp_rule.randomID === rule.randomID
                                                ? { ...gp_rule, field: null, field_label: null, condition: null, condition_label: null, value: null, condition_value_type: null  }
                                                : gp_rule
                                        ),
                                    }
                                    : con_group
                            ),
                        };
                    });
                }}
            >
                {
                    filterFields !== null ?
                        <div className={styles.ActiontypesDropdown}>
                            <WorkflowDataSelector
                                data={filterFields && filterFields}
                                onDataSelected={(value: any, data: any) => {
                                    setFilterFieldsQuery(' ')
                                    hideDropdownRef.current();
                                    setTopActionsDetails((prevState: any) => {
                                        return {
                                            ...prevState,
                                            // executed_resource_name: result,
                                            condition_groups: prevState && prevState.condition_groups.map((con_group: any) =>
                                                con_group.randomID === conditionGroup.randomID
                                                    ? {
                                                        ...con_group,
                                                        rules: con_group.rules.map((gp_rule: any) =>
                                                            gp_rule.randomID === rule.randomID
                                                                ?
                                                                {
                                                                    ...gp_rule,
                                                                    field: data.slug,
                                                                    field_label: data.label,
                                                                    filter_type: data.filter_type,
                                                                    category: data.category,
                                                                    community_field_type: data.community_field_type,
                                                                    condition_value_type: data.condition_value_type,
                                                                }
                                                                :
                                                                gp_rule
                                                        ),
                                                    }
                                                    : con_group
                                            ),
                                        };
                                    });

                                }}
                                searchString={filterFieldsQuery}
                                labelStyle={styles.workflowActionlabelStyle}
                                containerClassName={styles.ActiontypesDropdown__Dropdown}
                                elementContainerClass={styles.workflowActionEl}
                            />
                        </div>
                        :
                        <></>
                }
            </AppSelect>
        </div>
    )
}

export default RuleEditor;
